import addMonths from 'date-fns/addMonths'
import subMonths from 'date-fns/subMonths'
import getYear from 'date-fns/getYear'
import differenceInCalendarMonths from 'date-fns/differenceInCalendarMonths'

/**
 * Checks if next month is disabled
 * @param {Date} date - The current date
 * @param {Date} maxMonth- max allowed month
 * @returns {boolean}
 */
const isNextMonthDisabled = (date: Date, maxMonth: Date) => {
  const nextMonth = addMonths(date, 1)
  return differenceInCalendarMonths(nextMonth, maxMonth) > 0
}

/**
 * Checks if prev month is disabled
 * @param {Date} date - The current date
 * @param {Date} minMonth- max allowed month
 * @returns {boolean}
 */
const isPrevMonthDisabled = (date: Date, minMonth: Date) => {
  const prevMonth = subMonths(date, 1)
  return differenceInCalendarMonths(minMonth, prevMonth) > 0
}

const getYearsPeriod = (date, yearItemNumber = 9) => {
  const endPeriod = Math.ceil(getYear(date) / yearItemNumber) * yearItemNumber;
  const startPeriod = endPeriod - (yearItemNumber - 1);
  return { startPeriod, endPeriod };
}

const withInRangeSet = (sDate: any, eDate: any, currentDate: any) => {
  return (new Date(currentDate) >= sDate && new Date(currentDate) <= eDate)
}

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
]

const headerTestIds = {
  year: 'cs-date-picker-year',
  month: 'cs-date-picker-month'
}

const inputMaskDefaultValues = {
  hasInputMask: true,
  disabled: false,
  inputMaskTestId: `cs-date-input-mask`,
  maskType: "99-99-9999",
  maskPlaceholder: "mm-dd-yyyy",
  placeholder: "Date[mm-dd-yyyy]",
  classname: "",
  showErrorBorder: true
}

const getMaskValue = (inputMaskValue: string) => {
  let maskValueFormat = inputMaskValue.trim()
  if (maskValueFormat.includes(" ") && !maskValueFormat.includes("-")) maskValueFormat = inputMaskValue.split(" ").join('-')
  let monthLen = maskValueFormat.split('-')[0]?.trim()?.length || 0
  let dayLen = maskValueFormat.split('-')[1]?.trim()?.length || 0
  let yearLen = maskValueFormat.split('-')[2]?.length || 0
  if ((monthLen === 0 || monthLen === 1) && dayLen === 0 && yearLen === 0) {
    return "99 99 9999"
  } else if (yearLen === 0 && dayLen === 1) {
    return "99-99 9999"
  } else if (monthLen === 2 && yearLen === 0 && dayLen === 0) {
    return "99-99 9999"
  } else {
    return "99-99-9999"
  }
}

const getTimeMaskValue = (inputMaskValue: string) => {
  let maskValueFormat = inputMaskValue.includes('+') ? inputMaskValue.split('+')[0]?.trim() : inputMaskValue.split('-')[0]?.trim()
  let hhLen = maskValueFormat.split(':')[0]?.trim()?.length || 0
  let mmLen = maskValueFormat.split(':')[1]?.trim()?.length || 0
  let ssLen = maskValueFormat.split(':')[2]?.length || 0
  if ((hhLen === 0 || hhLen === 1) && mmLen === 0 && ssLen === 0) {
    return "99 99 99"
  } else if (ssLen === 0 && mmLen === 1) {
    return "99:99 99"
  } else if (hhLen === 2 && ssLen === 0 && mmLen === 0) {
    return "99:99 99"
  } else {
    return "99:99:99"
  }
}

const validDateRegex = /^(?:(?:(?:0?[13578]|1[02])(\/|-|\.)31)\1|(?:(?:0?[1,3-9]|1[0-2])(\/|-|\.)(?:29|30)\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:0?2(\/|-|\.)29\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:(?:0?[1-9])|(?:1[0-2]))(\/|-|\.)(?:0?[1-9]|1\d|2[0-8])\4(?:(?:1[6-9]|[2-9]\d)?\d{2})$/

/**
 * Formats a given time string to ensure it is in the format of hh:mm:ss.
 * Each part (hours, minutes, seconds) must be between 00 and 59.
 * 
 * @param {string} timeString - The time string to format.
 * @returns {string} The formatted time string.
 * @throws {Error} If the input is not in the expected format or contains invalid values.
 */
function formatTimeString(timeString) {
  const timeParts = timeString.split(':');

  if (timeParts.length !== 3) {
    throw new Error('Invalid time format. Expected format is hh:mm:ss');
  }

  const [hours, minutes, seconds] = timeParts.map(part => {
    const number = parseInt(part, 10);
    if (isNaN(number) || number < 0 || number > 59) {
      throw new Error('Invalid time value. Each part should be between 00 and 59.');
    }
    return number.toString().padStart(2, '0');
  });

  return `${hours}:${minutes}:${seconds}`;
}

export { isNextMonthDisabled, isPrevMonthDisabled, getYearsPeriod, months, validDateRegex, withInRangeSet, headerTestIds, inputMaskDefaultValues, getMaskValue, getTimeMaskValue, formatTimeString }